// Jobs, visits and job types' wire layer (PRD 90 Wave 1, §6), under
// /api/v1/business/field/. Called by the jobs list, the new-job drawer, the job
// page (its Visits, Files and Status log sections), the schedule drawer, the
// Record status drawer, the job-type settings page, and the location page's
// Jobs region.
//
//   GET    job-types?include_archived        POST job-types | PATCH job-types/:id
//   POST   job-types/:id/archive | unarchive
//   GET    job-types/library                 POST job-types/library/install {trade | keys[]}
//   GET    jobs?status&job_type_id&technician_id&customer_id&service_location_id&priority&source
//              &from&to&q&phone&cursor&limit
//   POST   jobs                              Idempotency-Key; first_visit places visit 1
//   GET    jobs/:id | PATCH jobs/:id | GET jobs/:id/events
//   POST   jobs/:job_id/visits               Add visit
//   PATCH  visits/:id/schedule               lock_version; 409 stale
//   POST   visits/:id/unschedule             lock_version; 409 stale
//   POST   visits/:id/dispatch
//   POST   visits/:id/events                 Record status (the office, on a technician's behalf)
//   GET|POST jobs/:job_id/documents; GET …/:document_id/download; DELETE …/:document_id
//   PRD 90 Wave 3: GET jobs/attribution · GET jobs/callback-candidates?service_location_id&job_type_id
//   · GET jobs/:id/callback-candidates · POST jobs/:id/follow-up (a technician; Idempotency-Key)
//
// EVERY RESPONSE IS PARSED. apiFetch never throws on a status, so a write that
// dropped its Response would turn 409 stale, 422 invalid_transition and 403
// module_required into a success. parseEnvelope throws BusinessApiError with
// the server's code, message and details instead; the readers at the bottom
// pull out the refusals a screen answers in kind.
//
// Who may call what is the api's (JobEndpoints' READ, OPERATE and DOCUMENT
// lists), mirrored in roleCopy.ts and each job's `permissions`.

import {
  businessApiDelete,
  businessApiGet,
  businessApiPatch,
  businessApiPost,
  businessApiPostForm,
} from '../utils/apiUtils';
import { BusinessApiError, buildQuery, parseData, parseEnvelope } from './businessApiCommon';
import type { UUID } from '../types/businessApi';
import type {
  AddVisitParams,
  BusinessUnitRef,
  FollowUpJob,
  FollowUpParams,
  JobAttributionOptions,
  JobLinkRef,
  JobCreateParams,
  JobDocument,
  JobListPage,
  JobListQuery,
  JobType,
  JobTypeBlueprint,
  JobTypeInstallResult,
  JobTypeList,
  JobTypeParams,
  JobUpdateParams,
  KeyLabel,
  SchedulingWarning,
  ServiceJob,
  ServiceJobDetail,
  ServiceVisit,
  TapParams,
  TapResult,
  VisitScheduleParams,
  VisitStatusEvent,
  VisitWrite,
} from '../types/fieldJobsApi';

const FIELD = '/api/v1/business/field';
const JOB_TYPES = `${FIELD}/job-types`;
const JOBS = `${FIELD}/jobs`;
const VISITS = `${FIELD}/visits`;
const jobTypePath = (id: UUID) => `${JOB_TYPES}/${encodeURIComponent(id)}`;
const jobPath = (id: UUID) => `${JOBS}/${encodeURIComponent(id)}`;
const visitPath = (id: UUID) => `${VISITS}/${encodeURIComponent(id)}`;

/** A schedule write made against an older lock_version: `details.current` is the visit as it now is. */
export const STALE_CODE = 'stale';
/** PRD 91: a placement with warnings the person has not confirmed. */
export const NEEDS_CONFIRMATION_CODE = 'needs_confirmation';
/** A press the assignment's state does not allow: `details` names whose and where they are. */
export const INVALID_TRANSITION_CODE = 'invalid_transition';
/** A press earlier than the assignment's latest event. */
export const OUT_OF_ORDER_CODE = 'out_of_order';
/** The technician is already on the way to, or at, another visit. */
export const ACTIVE_ELSEWHERE_CODE = 'active_elsewhere';
/** A time change while someone is on the way, on site or paused. */
export const VISIT_IN_PROGRESS_CODE = 'visit_in_progress';
export const JOB_ON_HOLD_CODE = 'job_on_hold';
/** A completed or cancelled job takes no new visit and no scheduling. */
export const JOB_NOT_OPEN_CODE = 'job_not_open';
/** A local time inside the spring-forward gap. */
export const NONEXISTENT_LOCAL_TIME_CODE = 'nonexistent_local_time';

/** A fresh key, for Idempotency-Key and client_event_id: a double press makes one row. */
export function newJobRequestKey(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  // RFC 4122 v4 shape, so the api's uuid check on client_event_id accepts it.
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${hex(3)}-${hex(12)}`;
}

const keyHeader = (key?: string) => (key ? { headers: { 'Idempotency-Key': key } } : undefined);

/** A binary GET: the bytes, or the server's refusal thrown as BusinessApiError. */
async function blobOf(response: Response, fallback: string): Promise<Blob> {
  if (!response.ok) {
    await parseData(response);
    throw new Error(fallback);
  }
  return response.blob();
}

export const jobTypeService = {
  /** Live types, in picker order; `includeArchived` adds the archived (ignored for a technician). */
  async list(includeArchived = false): Promise<JobTypeList> {
    const qs = includeArchived ? '?include_archived=true' : '';
    const { data, meta } = await parseEnvelope<JobType[]>(await businessApiGet(`${JOB_TYPES}${qs}`));
    const page = meta as { trades?: KeyLabel[]; priorities?: KeyLabel[]; business_units?: BusinessUnitRef[] };
    return {
      types: data ?? [],
      trades: page.trades ?? [],
      priorities: page.priorities ?? [],
      businessUnits: Array.isArray(page.business_units) ? page.business_units : [],
    };
  },

  async create(params: JobTypeParams): Promise<JobType> {
    return parseData<JobType>(await businessApiPost(JOB_TYPES, params));
  },

  /** Editing a type never changes a job already made: a job copied its defaults when it was created. */
  async update(id: UUID, params: JobTypeParams): Promise<JobType> {
    return parseData<JobType>(await businessApiPatch(jobTypePath(id), params));
  },

  async archive(id: UUID): Promise<JobType> {
    return parseData<JobType>(await businessApiPost(`${jobTypePath(id)}/archive`));
  },

  async unarchive(id: UUID): Promise<JobType> {
    return parseData<JobType>(await businessApiPost(`${jobTypePath(id)}/unarchive`));
  },

  /** Every blueprint, those for the business's trades (and General service) first. */
  async library(): Promise<JobTypeBlueprint[]> {
    return (await parseData<JobTypeBlueprint[]>(await businessApiGet(`${JOB_TYPES}/library`))) ?? [];
  },

  /** Copies blueprints in as the business's own types, idempotently. */
  async install(keys: string[]): Promise<JobTypeInstallResult> {
    return parseData<JobTypeInstallResult>(await businessApiPost(`${JOB_TYPES}/library/install`, { keys }));
  },
};

export const jobService = {
  /** Most recently changed first, keyset-paged; `total` counts every match, not just this page. */
  async list(query: JobListQuery = {}): Promise<JobListPage> {
    const { data, meta } = await parseEnvelope<ServiceJob[]>(await businessApiGet(`${JOBS}${buildQuery({ ...query })}`));
    const page = meta as { next_cursor?: string | null; total?: number };
    return { rows: data ?? [], nextCursor: page.next_cursor ?? null, total: page.total ?? (data ?? []).length };
  },

  async get(id: UUID): Promise<ServiceJobDetail> {
    return parseData<ServiceJobDetail>(await businessApiGet(jobPath(id)));
  },

  /** `replayed` is true when the key had already made the job — the same job comes back. */
  async create(params: JobCreateParams, idempotencyKey?: string): Promise<{ job: ServiceJobDetail; replayed: boolean }> {
    const { data, meta } = await parseEnvelope<ServiceJobDetail>(
      await businessApiPost(JOBS, params, keyHeader(idempotencyKey))
    );
    return { job: data, replayed: (meta as { replayed?: boolean }).replayed === true };
  },

  async update(id: UUID, params: JobUpdateParams): Promise<ServiceJobDetail> {
    return parseData<ServiceJobDetail>(await businessApiPatch(jobPath(id), params));
  },

  /** Every status change on every visit, oldest first (at most 1000). */
  async events(id: UUID): Promise<VisitStatusEvent[]> {
    return (await parseData<VisitStatusEvent[]>(await businessApiGet(`${jobPath(id)}/events`))) ?? [];
  },

  /** PRD 90 Wave 3: lead sources, business units and this business's recent campaigns. */
  async attribution(): Promise<JobAttributionOptions> {
    const data = await parseData<Partial<JobAttributionOptions>>(await businessApiGet(`${JOBS}/attribution`));
    return {
      lead_sources: data?.lead_sources ?? [],
      callback_kinds: data?.callback_kinds ?? [],
      business_units: data?.business_units ?? [],
      campaigns: data?.campaigns ?? [],
    };
  },

  /**
   * The new-job drawer's question (PRD 90 §4.9): completed jobs of this type at
   * this place inside the type's callback window. Suggested, never decided.
   */
  async suggestCallbacks(serviceLocationId: UUID, jobTypeId: UUID): Promise<JobLinkRef[]> {
    const qs = buildQuery({ service_location_id: serviceLocationId, job_type_id: jobTypeId });
    return (await parseData<JobLinkRef[]>(await businessApiGet(`${JOBS}/callback-candidates${qs}`))) ?? [];
  },

  /** The job page's picker: every earlier completed job at the place, the suggested ones flagged. */
  async callbackCandidates(id: UUID): Promise<JobLinkRef[]> {
    return (await parseData<JobLinkRef[]>(await businessApiGet(`${jobPath(id)}/callback-candidates`))) ?? [];
  },

  /** A technician's follow-up job: unscheduled, for the office to book. */
  async followUp(id: UUID, params: FollowUpParams, idempotencyKey?: string): Promise<FollowUpJob> {
    return parseData<FollowUpJob>(await businessApiPost(`${jobPath(id)}/follow-up`, params, keyHeader(idempotencyKey)));
  },

  /** A new visit, unscheduled unless the params place it. 422 job_not_open on a finished job. */
  async addVisit(jobId: UUID, params: AddVisitParams): Promise<VisitWrite> {
    return parseData<VisitWrite>(await businessApiPost(`${jobPath(jobId)}/visits`, params));
  },
};

export const visitService = {
  /** The one route that places a visit or changes its crew. 409 stale carries the visit as it now is. */
  async schedule(id: UUID, params: VisitScheduleParams): Promise<VisitWrite> {
    return parseData<VisitWrite>(await businessApiPatch(`${visitPath(id)}/schedule`, params));
  },

  /** Back to the tray: the crew comes off; date, slot and window clear; the due window stays. */
  async unschedule(id: UUID, lockVersion: number, notifyCustomer?: boolean): Promise<VisitWrite> {
    const body: { lock_version: number; notify_customer?: boolean } = { lock_version: lockVersion };
    if (notifyCustomer !== undefined) body.notify_customer = notifyCustomer;
    return parseData<VisitWrite>(await businessApiPost(`${visitPath(id)}/unschedule`, body));
  },

  /** Releases the visit to its crew. Idempotent: nobody waiting answers an empty list. */
  async dispatch(id: UUID): Promise<VisitWrite & { dispatchedTechnicianIds: UUID[] }> {
    const { data, meta } = await parseEnvelope<VisitWrite>(await businessApiPost(`${visitPath(id)}/dispatch`));
    const ids = (meta as { dispatched_technician_ids?: UUID[] }).dispatched_technician_ids;
    return { ...data, dispatchedTechnicianIds: Array.isArray(ids) ? ids : [] };
  },

  /** One press. A replayed client_event_id answers the stored events and writes nothing. */
  async recordEvent(id: UUID, params: TapParams): Promise<TapResult> {
    const { data, meta } = await parseEnvelope<VisitWrite & { events: TapResult['events'] }>(
      await businessApiPost(`${visitPath(id)}/events`, params)
    );
    return { ...data, events: data?.events ?? [], replayed: (meta as { replayed?: boolean }).replayed === true };
  },
};

export const jobDocumentService = {
  async list(jobId: UUID): Promise<JobDocument[]> {
    return (await parseData<JobDocument[]>(await businessApiGet(`${jobPath(jobId)}/documents`))) ?? [];
  },

  async upload(jobId: UUID, file: File, caption?: string): Promise<JobDocument> {
    const form = new FormData();
    form.append('file', file);
    form.append('role', 'supporting');
    if (caption) form.append('caption', caption);
    return parseData<JobDocument>(await businessApiPostForm(`${jobPath(jobId)}/documents`, form));
  },

  async download(jobId: UUID, documentId: UUID): Promise<Blob> {
    return blobOf(
      await businessApiGet(`${jobPath(jobId)}/documents/${encodeURIComponent(documentId)}/download`),
      'Could not download the file'
    );
  },

  /** Takes it off the job; the file stays in the business's library. */
  async remove(jobId: UUID, documentId: UUID): Promise<void> {
    await parseEnvelope<unknown>(
      await businessApiDelete(`${jobPath(jobId)}/documents/${encodeURIComponent(documentId)}`)
    );
  },
};

// ---- Reading refusals -----------------------------------------------------------

/** The server's sentence, or the fallback for anything that is not a refusal. */
export function jobRefusalMessage(error: unknown, fallback: string): string {
  return error instanceof BusinessApiError ? error.message : fallback;
}

/**
 * The warnings from a 422 `needs_confirmation` (PRD 91), or null for any other
 * error. The caller shows them and, on confirm, sends the same placement with
 * their codes in `acknowledged_warnings`.
 */
export function schedulingWarningsOf(error: unknown): SchedulingWarning[] | null {
  if (!(error instanceof BusinessApiError) || error.code !== NEEDS_CONFIRMATION_CODE) return null;
  const details = error.details as unknown as { warnings?: SchedulingWarning[] } | null;
  return Array.isArray(details?.warnings) && details!.warnings.length > 0 ? details!.warnings : null;
}

/** The visit as it now is, from a 409 `stale`; null for any other error. */
export function staleVisitOf(error: unknown): ServiceVisit | null {
  if (!(error instanceof BusinessApiError) || error.code !== STALE_CODE) return null;
  const details = error.details as unknown as { current?: ServiceVisit } | null;
  return details?.current ?? null;
}

/** `{ field: message }` from a 422 `validation_failed` (or any refusal naming fields). */
export function jobFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof BusinessApiError) || !Array.isArray(error.details)) return {};
  return Object.fromEntries(
    error.details
      .filter(detail => detail && typeof detail.field === 'string')
      .map(detail => [detail.field, detail.message])
  );
}

/** The array-shaped details of a tap refusal (`invalid_transition`, `out_of_order`, `active_elsewhere`). */
export function tapRefusalDetails<T>(error: unknown, code: string): T[] {
  if (!(error instanceof BusinessApiError) || error.code !== code) return [];
  const details = error.details as unknown;
  if (Array.isArray(details)) return details as T[];
  return details && typeof details === 'object' ? [details as T] : [];
}
